import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  Navigation,
  RefreshCcw,
  Truck,
  WifiOff,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { fetchRetailCatalog, fetchRetailInventory } from "@/lib/api/retail";
import {
  fetchFoodTruckStops,
  transitionFoodTruckStopStatus,
  type FoodTruckStop,
  type StopStatus,
} from "@/lib/api/operations";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOfflineQueueByKind } from "@/hooks/useOfflineQueue";
import { useToast } from "@/hooks/use-toast";
import { buildKitchenRows } from "@/features/inventory/utils/kitchenRows";
import { useOfflineRuntime } from '@/hooks/useOfflineRuntime';

type StopAction = {
  next: StopStatus;
  labelKey: string;
  defaultValue: string;
  icon: LucideIcon;
  variant?: "default" | "secondary" | "destructive";
};

const STOP_STATUS_PRIORITY: Record<StopStatus, number> = {
  on_site: 0,
  en_route: 1,
  scheduled: 2,
  completed: 3,
  canceled: 4,
};

const STOP_STATUS_TONE: Record<StopStatus, string> = {
  scheduled:
    "border border-slate-200/70 bg-slate-100/60 text-slate-700 dark:border-slate-700/60 dark:bg-slate-800/60 dark:text-slate-200",
  en_route:
    "border border-emerald-200/70 bg-emerald-100/60 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/15 dark:text-emerald-200",
  on_site:
    "border border-emerald-400/60 bg-emerald-500/15 text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-500/20 dark:text-emerald-200",
  completed:
    "border border-slate-200/70 bg-slate-50/80 text-slate-600 dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-200",
  canceled:
    "border border-rose-200/70 bg-rose-100/60 text-rose-700 dark:border-rose-500/35 dark:bg-rose-500/15 dark:text-rose-200",
};

const STOP_ACTIONS: Record<StopStatus, StopAction[]> = {
  scheduled: [
    {
      next: "en_route",
      labelKey: "retail.inventory.foodTruck.actions.depart",
      defaultValue: "Start drive",
      icon: Navigation,
      variant: "default",
    },
    {
      next: "canceled",
      labelKey: "retail.inventory.foodTruck.actions.cancel",
      defaultValue: "Cancel stop",
      icon: Ban,
      variant: "destructive",
    },
  ],
  en_route: [
    {
      next: "on_site",
      labelKey: "retail.inventory.foodTruck.actions.arrive",
      defaultValue: "Mark arrived",
      icon: MapPin,
      variant: "default",
    },
  ],
  on_site: [
    {
      next: "completed",
      labelKey: "retail.inventory.foodTruck.actions.wrap",
      defaultValue: "Wrap service",
      icon: CheckCircle2,
      variant: "default",
    },
  ],
  completed: [],
  canceled: [],
};

const queueKinds = ["inventory", "pos-order", "ops"] as const;

const resolveStopAddress = (stop: FoodTruckStop): string | null => {
  const parts = [
    stop.addressLine1,
    stop.addressLine2,
    stop.city,
    stop.region,
  ].filter((value): value is string => typeof value === "string" && value.trim().length > 0);

  if (parts.length === 0) {
    return null;
  }

  return parts.join(", ");
};

const resolveStopWindow = (
  stop: FoodTruckStop,
  dateFormatter: Intl.DateTimeFormat,
  timeFormatter: Intl.DateTimeFormat,
): string | null => {
  const start = new Date(stop.startAt);
  const end = new Date(stop.endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const sameDay = start.toDateString() === end.toDateString();
  const dateLabel = dateFormatter.format(start);
  const rangeLabel = `${timeFormatter.format(start)} – ${timeFormatter.format(end)}`;
  return sameDay ? `${dateLabel} · ${rangeLabel}` : `${dateFormatter.format(start)} – ${dateFormatter.format(end)}`;
};

const FoodTruckModePage = () => {
  const { t } = useTranslation(["common", "retail"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { formatNumber, formatRelativeTime } = useLocaleFormatter();

  const stopsQuery = useQuery({
    queryKey: ["ops", "food-truck-stops"],
    queryFn: ({ signal }) => fetchFoodTruckStops({ signal }),
    refetchInterval: 60_000,
  });

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
  });

  const inventoryQuery = useQuery({
    queryKey: ["retail", "inventory"],
    queryFn: ({ signal }) => fetchRetailInventory({ signal }),
  });

  const inventoryQueue = useOfflineQueueByKind("inventory");
  const posQueue = useOfflineQueueByKind("pos-order");
  const opsQueue = useOfflineQueueByKind("ops");
  const { enabled: offlineRuntimeEnabled } = useOfflineRuntime();

  const latestSyncedAt = useMemo(() => {
    let latest = 0;
    for (const queue of [inventoryQueue, posQueue, opsQueue]) {
      for (const entry of queue.synced) {
        if (typeof entry.completedAt === "number" && entry.completedAt > latest) {
          latest = entry.completedAt;
        }
      }
    }
    return latest > 0 ? latest : null;
  }, [inventoryQueue, posQueue, opsQueue]);

  const offlinePending = useMemo(() => {
    return (
      inventoryQueue.queued.length +
      inventoryQueue.retrying.length +
      posQueue.queued.length +
      posQueue.retrying.length +
      opsQueue.queued.length +
      opsQueue.retrying.length
    );
  }, [inventoryQueue, posQueue, opsQueue]);

  const retryingCount = useMemo(
    () => inventoryQueue.retrying.length + posQueue.retrying.length + opsQueue.retrying.length,
    [inventoryQueue.retrying.length, posQueue.retrying.length, opsQueue.retrying.length],
  );

  const manifestRows = useMemo(() => {
    const rows = buildKitchenRows(inventoryQuery.data ?? [], catalogQuery.data ?? []);
    return rows
      .filter((row) => row.status !== "healthy")
      .sort((left, right) => right.available - left.available)
      .slice(0, 5);
  }, [catalogQuery.data, inventoryQuery.data]);

  const manifestLoading = catalogQuery.isLoading || inventoryQuery.isLoading;

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
      }),
    [],
  );
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
      }),
    [],
  );

  const stops = useMemo(() => {
    const list = stopsQuery.data ?? [];
    return [...list].sort((left, right) => {
      const priorityDelta = STOP_STATUS_PRIORITY[left.status] - STOP_STATUS_PRIORITY[right.status];
      if (priorityDelta !== 0) {
        return priorityDelta;
      }
      return new Date(left.startAt).getTime() - new Date(right.startAt).getTime();
    });
  }, [stopsQuery.data]);

  const activeStops = stops.filter((stop) => stop.status === "on_site");
  const upcomingStops = stops.filter((stop) => stop.status === "scheduled" || stop.status === "en_route");

  const statusMutation = useMutation({
    mutationFn: ({ stopId, status }: { stopId: string; status: StopStatus }) =>
      transitionFoodTruckStopStatus(stopId, status),
    onSuccess: (result, variables) => {
      const statusLabel = t(`retail.inventory.foodTruck.stops.status.${variables.status}`, {
        defaultValue: variables.status,
      });
      if (result.queued) {
        toast({
          title: t("retail.inventory.foodTruck.toast.statusQueued.title", {
            defaultValue: "Queued for sync",
          }),
          description: t("retail.inventory.foodTruck.toast.statusQueued.description", {
            defaultValue: "The update will sync automatically once you are back online.",
            status: statusLabel,
          }),
        });
      } else if (result.stop) {
        toast({
          title: t("retail.inventory.foodTruck.toast.statusUpdated.title", {
            defaultValue: "Stop updated",
          }),
          description: t("retail.inventory.foodTruck.toast.statusUpdated.description", {
            defaultValue: "Marked as {{status}}.",
            status: statusLabel,
          }),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["ops", "food-truck-stops"] });
    },
    onError: (error, variables) => {
      console.error("[food-truck] status update failed", error);
      toast({
        variant: "destructive",
        title: t("retail.inventory.foodTruck.toast.statusError.title", {
          defaultValue: "Update failed",
        }),
        description:
          error instanceof Error
            ? error.message
            : t("retail.inventory.foodTruck.toast.statusError.description", {
                defaultValue: "Unable to transition to {{status}}. Try again shortly.",
                status: variables.status,
              }),
      });
    },
  });

  const mutationInFlight = statusMutation.isPending;
  const pendingVariables = statusMutation.variables;

  return (
    <div className="flex flex-col gap-6 pb-16">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <Badge className="border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-200">
            {t("retail.inventory.foodTruck.previewBadge", { defaultValue: "Truck mode" })}
          </Badge>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {t("retail.inventory.foodTruck.title", { defaultValue: "Food truck operations" })}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("retail.inventory.foodTruck.subtitle", {
              defaultValue: "Coordinate service windows, restock prep batches, and stay resilient offline.",
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => stopsQuery.refetch()}
            disabled={stopsQuery.isFetching}
            className="gap-2"
          >
            {stopsQuery.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            {t("retail.inventory.foodTruck.actions.refresh", { defaultValue: "Refresh" })}
          </Button>
        </div>
      </header>

      <section
        className={cn(
          "grid gap-4",
          isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3",
        )}
      >
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
              <Truck className="h-3.5 w-3.5" />
              {t("retail.inventory.foodTruck.summary.active.label", { defaultValue: "Active service" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
              {formatNumber(activeStops.length)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.foodTruck.summary.active.description", {
                defaultValue: "Trucks currently serving guests.",
              })}
            </p>
          </CardHeader>
        </Card>

        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-600 dark:text-slate-300">
              <Clock className="h-3.5 w-3.5" />
              {t("retail.inventory.foodTruck.summary.upcoming.label", { defaultValue: "Upcoming windows" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
              {formatNumber(upcomingStops.length)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.foodTruck.summary.upcoming.description", {
                defaultValue: "Scheduled or en route service windows.",
              })}
            </p>
          </CardHeader>
        </Card>

        {offlineRuntimeEnabled ? (
          <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
            <CardHeader className="space-y-1">
              <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-600 dark:text-slate-300">
                <WifiOff className="h-3.5 w-3.5" />
                {t("retail.inventory.foodTruck.summary.offline.label", { defaultValue: "Offline queue" })}
              </CardDescription>
              <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {formatNumber(offlinePending)}
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {offlinePending > 0
                  ? t("retail.inventory.foodTruck.summary.offline.pending", {
                      defaultValue: "{{count}} updates waiting to sync.",
                      count: offlinePending,
                    })
                  : t("retail.inventory.foodTruck.summary.offline.clear", {
                      defaultValue: "All updates synced.",
                    })}
              </p>
              {latestSyncedAt ? (
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  {t("retail.inventory.foodTruck.summary.offline.lastSynced", {
                    defaultValue: "Last sync {{time}} ago.",
                    time: formatRelativeTime(latestSyncedAt),
                  })}
                </p>
              ) : null}
            </CardHeader>
          </Card>
        ) : null}
      </section>

      {offlineRuntimeEnabled && offlinePending > 0 ? (
        <Alert variant={retryingCount > 0 ? "destructive" : "default"}>
          <AlertTitle>
            {retryingCount > 0
              ? t("retail.inventory.foodTruck.offline.alert.retryingTitle", { defaultValue: "Retrying updates" })
              : t("retail.inventory.foodTruck.offline.alert.pendingTitle", {
                  defaultValue: "Queued for sync",
                })}
          </AlertTitle>
          <AlertDescription className="space-y-2 text-sm">
            <p>
              {retryingCount > 0
                ? t("retail.inventory.foodTruck.offline.alert.retryingDescription", {
                    defaultValue:
                      "{{count}} update(s) will retry automatically once connectivity returns.",
                    count: retryingCount,
                  })
                : t("retail.inventory.foodTruck.offline.alert.pendingDescription", {
                    defaultValue:
                      "{{count}} update(s) stored locally. Stay on this screen to sync once online.",
                    count: offlinePending,
                  })}
            </p>
            <div className="flex flex-wrap gap-2">
              {queueKinds.map((kind) => {
                const queue =
                  kind === "inventory" ? inventoryQueue : kind === "pos-order" ? posQueue : opsQueue;
                const count = queue.queued.length + queue.retrying.length;
                if (count === 0) {
                  return null;
                }
                return (
                  <Badge key={kind} variant="outline">
                    {t(`retail.inventory.foodTruck.offline.badges.${kind}`, {
                      defaultValue: kind,
                    })}
                    : {count}
                  </Badge>
                );
              })}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-[2fr,1fr]">
        <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
                {t("retail.inventory.foodTruck.stops.title", { defaultValue: "Service windows" })}
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-slate-400">
                {t("retail.inventory.foodTruck.stops.subtitle", {
                  defaultValue: "Move through checklists and track truck readiness in real time.",
                })}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stopsQuery.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : stops.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {t("retail.inventory.foodTruck.stops.empty", {
                  defaultValue: "No scheduled stops. Add one from the operations dashboard.",
                })}
              </div>
            ) : (
              <div className="space-y-3">
                {stops.map((stop) => {
                  const actions = STOP_ACTIONS[stop.status];
                  const address = resolveStopAddress(stop);
                  const windowLabel = resolveStopWindow(stop, dateFormatter, timeFormatter);
                  return (
                    <div
                      key={stop.id}
                      className="rounded-lg border border-slate-200/70 bg-white/95 p-4 shadow-sm transition hover:border-emerald-200/70 hover:shadow-md dark:border-slate-800/60 dark:bg-slate-950/60 dark:hover:border-emerald-400/40"
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {stop.label}
                            </h3>
                            <Badge className={STOP_STATUS_TONE[stop.status]}>
                              {t(`retail.inventory.foodTruck.stops.status.${stop.status}`, {
                                defaultValue: stop.status,
                              })}
                            </Badge>
                          </div>
                          {address ? (
                            <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <MapPin className="h-4 w-4" />
                              <span>{address}</span>
                            </p>
                          ) : null}
                          {windowLabel ? (
                            <p className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <Clock className="h-3.5 w-3.5" />
                              <span>{windowLabel}</span>
                            </p>
                          ) : null}
                          {typeof stop.windowNotes === "string" && stop.windowNotes.trim().length > 0 ? (
                            <p className="rounded-md border border-amber-200/70 bg-amber-50/70 p-2 text-xs text-amber-700 dark:border-amber-400/35 dark:bg-amber-500/10 dark:text-amber-200">
                              {stop.windowNotes}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {actions.map((action) => {
                            const isActive =
                              mutationInFlight &&
                              pendingVariables?.stopId === stop.id &&
                              pendingVariables.status === action.next;
                            return (
                              <Button
                                key={action.next}
                                size="sm"
                                variant={action.variant ?? "secondary"}
                                className="gap-2"
                                disabled={mutationInFlight && !isActive}
                                onClick={() => statusMutation.mutate({ stopId: stop.id, status: action.next })}
                              >
                                {isActive ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <action.icon className="h-3.5 w-3.5" />
                                )}
                                {t(action.labelKey, { defaultValue: action.defaultValue })}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              {t("retail.inventory.foodTruck.manifest.title", { defaultValue: "Restock manifest" })}
            </CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">
              {t("retail.inventory.foodTruck.manifest.subtitle", {
                defaultValue: "Low prep or ingredient items to bring onboard before service.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {manifestLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : manifestRows.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {t("retail.inventory.foodTruck.manifest.empty", {
                  defaultValue: "All tracked items are above par.",
                })}
              </div>
            ) : (
              <div className="space-y-3">
                {manifestRows.map((row) => (
                  <div
                    key={row.level.id ?? `${row.classification.id}:${row.level.locationId}`}
                    className="rounded-md border border-slate-200/70 bg-white/95 p-3 shadow-sm dark:border-slate-800/60 dark:bg-slate-950/60"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          {row.level.name ?? row.product?.name ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" })}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {row.product?.supplierName ??
                            row.level.locationName ??
                            t("retail.inventory.foodTruck.manifest.unknownSource", { defaultValue: "Prep kitchen" })}
                        </p>
                      </div>
                      <Badge className="border border-rose-200/70 bg-rose-100/60 text-rose-700 dark:border-rose-500/35 dark:bg-rose-500/15 dark:text-rose-200">
                        {t(`retail.inventory.foodTruck.manifest.status.${row.status}`, {
                          defaultValue: row.status,
                        })}
                      </Badge>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>
                        {t("retail.inventory.foodTruck.manifest.onHand", { defaultValue: "On hand" })}:
                        {" "}
                        {formatNumber(row.available)}{" "}
                        {row.classification.displayUnit?.abbreviation ?? row.classification.unit?.abbreviation ?? "ea"}
                      </span>
                      <span>
                        {t("retail.inventory.foodTruck.manifest.target", { defaultValue: "Par" })}:
                        {" "}
                        {row.reorderPoint !== null ? formatNumber(row.reorderPoint) : "-"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default FoodTruckModePage;
